// Iter 83 / Phase E4 Sprint 1 — Salary Advances Console.
// Interest-free advances with N-month recovery schedule. Simpler than
// loans: no amortisation, no foreclose, but same 4-eye approval (D14).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Plus, CheckCircle, XCircle, MagnifyingGlass, HandCoins, ListBullets,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const STATUS_TONE = {
    Requested: "bg-amber-100 text-amber-800",
    Approved: "bg-sky-100 text-sky-800",
    Active: "bg-emerald-100 text-emerald-800",
    Closed: "bg-slate-200 text-slate-700",
    Rejected: "bg-zinc-100 text-zinc-600",
};

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdvancesConsole() {
    const { can } = useAuth();
    const canIssue = can("loans:admin");
    const canApprove = can("loans:admin") || can("finance:approve");
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [issueOpen, setIssueOpen] = useState(false);
    const [issueForm, setIssueForm] = useState({ employee_id: "", amount: 10000, recovery_months: 3, purpose: "" });
    const [detail, setDetail] = useState(null);
    const [schedule, setSchedule] = useState([]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const [list, m, emp] = await Promise.all([
                api.get("/advances/list", { params }),
                api.get("/loans/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }).catch(() => ({ data: { items: [] } })),
            ]);
            setItems(list.data.items);
            setMeta(m.data);
            setEmployees(emp.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load advances"));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);
    useEffect(() => { reload(); }, [reload]);

    const filtered = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((it) =>
            (it.id || "").toLowerCase().includes(q)
            || (it.employee_name_snapshot || "").toLowerCase().includes(q)
        );
    }, [items, search]);

    const issue = async () => {
        if (!issueForm.employee_id) {
            toast.error("Employee is required");
            return;
        }
        try {
            const payload = {
                ...issueForm,
                amount: Number(issueForm.amount),
                recovery_months: Number(issueForm.recovery_months),
            };
            await api.post("/advances/request", payload);
            toast.success("Advance request created");
            setIssueOpen(false);
            setIssueForm({ employee_id: "", amount: 10000, recovery_months: 3, purpose: "" });
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Request failed"));
        }
    };

    const openDetail = async (adv) => {
        setDetail(adv);
        setSchedule([]);
        try {
            const s = await api.get(`/advances/${adv.id}/schedule`);
            setSchedule(s.data.items);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load schedule"));
        }
    };

    const approve = async (adv) => {
        if (!window.confirm(`Approve advance ${adv.id} for ${adv.employee_name_snapshot}?`)) return;
        try {
            await api.post(`/advances/${adv.id}/approve`, { approver_note: "Approved via console" });
            toast.success("Approved");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Approve failed"));
        }
    };

    const reject = async (adv) => {
        const reason = window.prompt("Reject reason:");
        if (!reason) return;
        try {
            await api.post(`/advances/${adv.id}/reject`, { reason });
            toast.success("Rejected");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Reject failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="advances-console-page">
            <PageHeader
                eyebrow="Loans & Advances"
                title="Salary Advances"
                description={`${items.length} advance${items.length === 1 ? "" : "s"} · Interest-free · auto-recovered from payroll`}
                actions={canIssue ? (
                    <Button onClick={() => setIssueOpen(true)} size="sm" data-testid="advance-new-btn">
                        <Plus size={14} className="mr-1" /> New Advance
                    </Button>
                ) : null}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="flex-1 min-w-[240px]">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Search</Label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input className="pl-9" placeholder="Advance ID or employee…"
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                data-testid="advances-search" />
                        </div>
                    </div>
                    <div className="w-48">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2"
                            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                            data-testid="advances-status-filter">
                            <option value="">All</option>
                            {(meta?.advance_states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="advances-table">
                {loading ? <div className="text-center py-12 text-sm text-slate-400">Loading…</div> : (
                    <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Advance ID</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-right px-3 py-2 font-medium">Amount</th>
                                <th className="text-right px-3 py-2 font-medium">Recovery</th>
                                <th className="text-right px-3 py-2 font-medium">Outstanding</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Requested</th>
                                <th className="text-left px-3 py-2 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`advance-row-${it.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{it.id}</td>
                                    <td className="px-3 py-2">{it.employee_name_snapshot}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(it.amount)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{it.recovery_months}m</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(it.outstanding_amount)}</td>
                                    <td className="px-3 py-2"><Badge className={`text-[10px] ${STATUS_TONE[it.status] || ""}`}>{it.status}</Badge></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{(it.requested_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2">
                                        <Button variant="ghost" size="sm" onClick={() => openDetail(it)} data-testid={`advance-view-${it.id}`}>
                                            <ListBullets size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-400 italic">No advances match.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Issue dialog */}
            <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
                <DialogContent className="max-w-md" data-testid="advance-issue-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><HandCoins size={18} /> New Salary Advance</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="col-span-2">
                            <Label>Employee *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={issueForm.employee_id}
                                onChange={(e) => setIssueForm({ ...issueForm, employee_id: e.target.value })}
                                data-testid="advance-issue-emp">
                                <option value="">— Select —</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.id})</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Amount (₹) *</Label>
                            <Input type="number" min="1" value={issueForm.amount}
                                onChange={(e) => setIssueForm({ ...issueForm, amount: e.target.value })}
                                data-testid="advance-issue-amount" />
                        </div>
                        <div>
                            <Label>Recovery (months) *</Label>
                            <Input type="number" min="1" max="24" value={issueForm.recovery_months}
                                onChange={(e) => setIssueForm({ ...issueForm, recovery_months: e.target.value })}
                                data-testid="advance-issue-months" />
                        </div>
                        <div className="col-span-2">
                            <Label>Purpose</Label>
                            <Input value={issueForm.purpose}
                                onChange={(e) => setIssueForm({ ...issueForm, purpose: e.target.value })}
                                placeholder="optional" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIssueOpen(false)}>Cancel</Button>
                        <Button onClick={issue} data-testid="advance-issue-submit">Submit Request</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="advance-detail-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <HandCoins size={18} /> {detail.id}
                                    <Badge className={`text-[10px] ${STATUS_TONE[detail.status] || ""} ml-2`}>{detail.status}</Badge>
                                </DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Employee</div>
                                    <div>{detail.employee_name_snapshot}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Amount · Recovery</div>
                                    <div className="font-mono">{fmtMoney(detail.amount)} · {detail.recovery_months}m</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Outstanding · Recovered</div>
                                    <div className="font-mono">{fmtMoney(detail.outstanding_amount)} · {detail.instalments_recovered || 0}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Applicant</div>
                                    <div className="text-xs">{detail.applicant_name}</div>
                                </div>
                                {detail.purpose && (
                                    <div className="col-span-2">
                                        <div className="text-[10px] uppercase text-slate-500 font-semibold">Purpose</div>
                                        <div className="text-sm italic">{detail.purpose}</div>
                                    </div>
                                )}
                            </div>
                            {detail.approval && (
                                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs">
                                    <div className="font-semibold text-emerald-800">Approved by {detail.approval.approver_name}</div>
                                    <div className="text-emerald-700">{detail.approval.approved_at}</div>
                                </div>
                            )}
                            {detail.rejection && (
                                <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded text-xs">
                                    <div className="font-semibold text-rose-800">Rejected by {detail.rejection.rejector_name}</div>
                                    <div className="text-slate-700 mt-1">{detail.rejection.reason}</div>
                                </div>
                            )}
                            <div className="flex gap-2 mt-3">
                                {detail.status === "Requested" && canApprove && (
                                    <>
                                        <Button size="sm" onClick={() => approve(detail)} data-testid="advance-approve-btn">
                                            <CheckCircle size={14} className="mr-1" /> Approve
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => reject(detail)} data-testid="advance-reject-btn">
                                            <XCircle size={14} className="mr-1" /> Reject
                                        </Button>
                                    </>
                                )}
                            </div>
                            <div className="mt-4">
                                <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Recovery Schedule</div>
                                <div className="overflow-x-auto border border-slate-200 rounded">
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50 text-slate-600">
                                            <tr>
                                                <th className="text-left px-2 py-1">#</th>
                                                <th className="text-right px-2 py-1">Amount</th>
                                                <th className="text-left px-2 py-1">Status</th>
                                                <th className="text-left px-2 py-1">Paid at</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schedule.map((r) => (
                                                <tr key={r.id} className="border-t border-slate-100 font-mono">
                                                    <td className="px-2 py-1">{r.instalment_no}</td>
                                                    <td className="px-2 py-1 text-right">{fmtMoney(r.amount)}</td>
                                                    <td className="px-2 py-1">{r.status}</td>
                                                    <td className="px-2 py-1 text-slate-500">{(r.paid_at || "").slice(0, 10) || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
